import React from 'react';
import RadioField from 'components/pure/utils/form/RadioField';
import {TrendType} from 'records/TriggerTrend';
import classNames from 'classnames';
import CountChart from './CountChart';
import AverageChart from './AverageChart';
import PercentageChart from './PercentageChart';
import comparatorOptions from 'utils/const/comparatorOptions';
import PercentageTriggerActionSelector from './PercentageTriggerActionSelector';
import periodOptions from './periodOptions';
import subPeriodOptions from './subPeriodOptions';
import InputField from 'components/pure/utils/form/InputField';
import SelectField from 'components/pure/utils/form/SelectField';
import './CreateTrendAction.styl';
import hasFeature from 'utils/hasFeature';
import ConstValues from 'utils/const';
import AuthStore from 'stores/AuthStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import UnsavedTriggerStore from 'stores/triggers/UnsavedTriggerStore';
import UnsavedTriggerActions from 'actions/triggers/UnsavedTriggerActions';

@connectToStores
export default class CreateTrendAction extends React.PureComponent {
  static getStores() {
    return [UnsavedTriggerStore];
  }

  static getPropsFromStores() {
    const trend = UnsavedTriggerStore.getState().trend;
    return {unsavedTrend: trend};
  }

  constructor(props) {
    super(props);

    this.selectedPeriodChange = this.selectedPeriodChange.bind(this);
    this.selectedTrendChange = this.selectedTrendChange.bind(this);
    this.selectedAverageChange = this.selectedAverageChange.bind(this);
    this.selectedAntiRuleChange = this.selectedAntiRuleChange.bind(this);
    this.onComparatorChange = this.onComparatorChange.bind(this);
    this.onComparisonChange = this.onComparisonChange.bind(this);

    this.state = {
      // keep track if either selectedComparisonValue or selectedComparator has changed and
      // if so stop using defaults
      usingDefault: true,
    };
  }

  onComparatorChange(selectedComparator) {
    UnsavedTriggerActions.setInequalityType(selectedComparator);
    this.setState({usingDefault: false});
  }

  onComparisonChange(selectedComparisonValue) {
    UnsavedTriggerActions.setThreshold(selectedComparisonValue);
    this.setState({usingDefault: false});
  }

  selectedPeriodChange(selectedPeriod) {
    UnsavedTriggerActions.setPeriod(selectedPeriod);
    this.setState({usingDefault: false});
  }

  selectedAverageChange(selectedSubPeriod) {
    UnsavedTriggerActions.setSubPeriod(selectedSubPeriod);
    this.setState({usingDefault: false});
  }

  selectedAntiRuleChange(selectedAntiRule) {
    // TODO implement this once percentage trends are re-enabled
  }

  selectedTrendChange(trendType) {
    UnsavedTriggerActions.setTrendType(trendType, this.state.usingDefault);
    this.setState({usingDefault: false});
  }

  componentDidMount() {
    const {trend, unsavedTrend} = this.props;
    if (!trend) return;
    if (trend.id === unsavedTrend.id) return;

    // Without this setTimeout this change doesn't trigger a re-render for some reason
    setTimeout(() => {
      UnsavedTriggerActions.setTrend(trend);
    });
  }

  /**
   * Sometimes the anti-rules are not here right away, hence we are kind of repeating what's done in componentDidMount,
   * so that selectedAntiRule is set and the antirules dropdown know what to show
   */
  componentDidUpdate(prevProps) {
    const {trend, antiRules} = this.props;
    const prevAntiRulesSize = prevProps.antiRules ? prevProps.antiRules.size : 0;
    const curAntiRulesSize = antiRules ? antiRules.size : 0;
    if (prevAntiRulesSize !== curAntiRulesSize && antiRules) {
      const trendAntiRuleId = trend ? trend.antiRuleId : null;
      this.setState({
        selectedAntiRule: antiRules.find(a => a.id === trendAntiRuleId) || antiRules.get(0),
      });
    }
  }

  render() {
    const sessionUser = AuthStore.getState().user;
    const {rule, antiRules, unsavedTrend} = this.props;
    if (!antiRules) return null;

    const selectedAntiRule =
      antiRules.find(a => a.id === unsavedTrend.antiRuleId) || antiRules.get(0);
    const trendType = unsavedTrend.trendType;

    const selectedPeriodOption = periodOptions.find(o => o.value === unsavedTrend.period);

    const selectedAverageOption = subPeriodOptions.find(o => o.value === unsavedTrend.subPeriod);

    const selectedComparatorOption = comparatorOptions.find(
      o => o.value === unsavedTrend.inequalityType
    );

    /** Hide pct trends for now - they're broke. lgray 1/17/20 **/
    const hasFeaturePct = hasFeature(sessionUser.flags, 'percent_trend');
    const hasAntiRules = antiRules && antiRules.size > 0;
    const countLabel = 'Count of times';
    const averageLabel = 'Average number of times';
    const percentageLabel = 'Percentage of times';
    return (
      <div className={'trend-action-container' + (this.state.trendType ? ' chart-shown' : '')}>
        <div className="trend-action">
          <div className="title">
            Trend <span>How often the event occurs</span>
          </div>
          <div className="config-option">
            <div className="config-option-title">Select measurement period</div>
            <SelectField
              hideResetButton
              className="measurement-period"
              options={periodOptions}
              value={unsavedTrend.period}
              onChange={this.selectedPeriodChange}
            />
          </div>
          <div className="config-option" ref={c => (this.chartAnchor = c)}>
            <div className="config-option-title">Define Trend</div>
            <div className="config-option-item">
              <RadioField
                checked={trendType === TrendType.COUNT_OF_TIMES}
                label={
                  <div>
                    {trendType !== TrendType.COUNT_OF_TIMES && <span>{countLabel}</span>}
                    {trendType === TrendType.COUNT_OF_TIMES && (
                      <div className="option-description">
                        {countLabel} that this event occurs over {selectedPeriodOption.label}
                      </div>
                    )}
                  </div>
                }
                onClick={() => this.selectedTrendChange(TrendType.COUNT_OF_TIMES)}
              />
            </div>
            <div className="config-option-item">
              <RadioField
                checked={trendType === TrendType.AVERAGE_OF_TIMES}
                label={
                  <div>
                    {trendType !== TrendType.AVERAGE_OF_TIMES && <span>{averageLabel}</span>}
                    {trendType === TrendType.AVERAGE_OF_TIMES && (
                      <div className="option-description">
                        {averageLabel} that this event occurs over {selectedPeriodOption.label}
                      </div>
                    )}
                  </div>
                }
                onClick={() => this.selectedTrendChange(TrendType.AVERAGE_OF_TIMES)}
              />
              {trendType === TrendType.AVERAGE_OF_TIMES && (
                <div className="option-expanded">
                  <div>
                    per&nbsp;
                    <SelectField
                      hideResetButton
                      className="average"
                      options={subPeriodOptions}
                      value={unsavedTrend.subPeriod}
                      onChange={this.selectedAverageChange}
                    />
                  </div>
                </div>
              )}
            </div>
            {hasFeaturePct && hasAntiRules && (
              <div className="config-option-item">
                <RadioField
                  checked={trendType === TrendType.PERCENTAGE_OF_TIMES}
                  label={
                    <div>
                      {trendType !== TrendType.PERCENTAGE_OF_TIMES && (
                        <span>{percentageLabel}</span>
                      )}
                      {trendType === TrendType.PERCENTAGE_OF_TIMES && (
                        <div className="option-description">
                          {percentageLabel} that this event occurs over {selectedPeriodOption.label}{' '}
                          vs
                        </div>
                      )}
                    </div>
                  }
                  onClick={() => this.selectedTrendChange(TrendType.PERCENTAGE_OF_TIMES)}
                />
                {trendType === TrendType.PERCENTAGE_OF_TIMES && (
                  <div className="option-expanded">
                    <SelectField
                      hideResetButton
                      options={this.props.antiRules.map(rule => ({
                        value: rule,
                        label: rule.antiRuleShortName,
                      }))}
                      value={this.state.selectedAntiRule}
                      onChange={this.selectedAntiRuleChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {trendType && (
            <div className="config-option equality">
              <div className="config-option-title">When should the trend trigger an action</div>
              <div className="equality-definition">
                {trendType === TrendType.COUNT_OF_TIMES && (
                  <div>
                    When the total number of times over {selectedPeriodOption.label} that this event
                    occurs is:
                    <div className="when-trend-trigger-selector">
                      <SelectField
                        hideResetButton
                        className="equality-options"
                        options={comparatorOptions}
                        value={unsavedTrend.inequalityType}
                        onChange={this.onComparatorChange}
                        dropdownDirection={ConstValues.reactSelectize.dropdownDirection.up}
                      />
                      <InputField
                        type="number"
                        min={0}
                        max={1000}
                        value={unsavedTrend.threshold || ''}
                        onChange={this.onComparisonChange}
                      />{' '}
                      <span>times</span>
                    </div>
                  </div>
                )}
                {trendType === TrendType.AVERAGE_OF_TIMES && (
                  <div>
                    When the average number of times per{' '}
                    {selectedAverageOption.label && selectedAverageOption.label.toLowerCase()} over
                    a {selectedPeriodOption.label} period that this event occurs is:
                    <div className="when-trend-trigger-selector">
                      <SelectField
                        hideResetButton
                        className="equality-options"
                        options={comparatorOptions}
                        value={unsavedTrend.inequalityType}
                        onChange={this.onComparatorChange}
                        dropdownDirection={ConstValues.reactSelectize.dropdownDirection.up}
                      />
                      <InputField
                        type="number"
                        min={0}
                        max={1000}
                        value={unsavedTrend.threshold || ''}
                        onChange={this.onComparisonChange}
                      />
                    </div>
                  </div>
                )}
                {trendType === TrendType.PERCENTAGE_OF_TIMES && (
                  <PercentageTriggerActionSelector
                    {...{
                      trendType,
                      selectedComparator: unsavedTrend.inequalityType,
                      selectedAntiRule,
                      rule,
                      selectedComparisonValue: unsavedTrend.threshold,
                      selectedPeriodOption,
                    }}
                    onComparatorChange={this.onComparatorChange}
                    onComparisonChange={this.onComparisonChange}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className={classNames('chart-popup-container', {
            percentage: trendType === TrendType.PERCENTAGE_OF_TIMES,
          })}>
          {trendType === TrendType.COUNT_OF_TIMES && (
            <CountChart
              value={unsavedTrend.threshold}
              equality={selectedComparatorOption.label}
              period={selectedPeriodOption.label}
            />
          )}
          {trendType === TrendType.AVERAGE_OF_TIMES && (
            <AverageChart
              value={unsavedTrend.threshold}
              averageOption={selectedAverageOption.label}
              equality={selectedComparatorOption.label}
              period={selectedPeriodOption.label}
            />
          )}
          {trendType === TrendType.PERCENTAGE_OF_TIMES && (
            <PercentageChart
              percentage={unsavedTrend.threshold}
              comparator={selectedComparatorOption.label}
              period={selectedPeriodOption.label}
              baseLabel={selectedAntiRule && selectedAntiRule.ruleShortName}
              goalLabel={selectedAntiRule && selectedAntiRule.antiRuleShortName}
            />
          )}
        </div>
      </div>
    );
  }
}
